import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';

function ContactUs() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId } = useBranchFilter();

  const [contactDetails, setContactDetails] = useState({
    phone_number: '',
    email: '',
    address: '',
    website: '',
    office_hours: ''
  });

  const [faqs, setFaqs] = useState([]);
  const [showFaqForm, setShowFaqForm] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [faqForm, setFaqForm] = useState({ question: '', answer: '' });
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const sd = localStorage.getItem('schoolData');
    if (!sd) {
      navigate('/schoollogin');
      return;
    }
    setSchoolData(JSON.parse(sd));
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (schoolData?.id) {
      fetchContactDetails();
      fetchFAQs();
    }
  }, [schoolData?.id, selectedBranchId]);

  const fetchContactDetails = async () => {
    try {
      let query = supabase
        .from('school_contact_details')
        .select('*')
        .eq('school_id', schoolData.id)
        .maybeSingle();

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      } else {
        query = query.is('branch_id', null);
      }

      const { data, error } = await query;
      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching contact details:', error);
      } else if (data) {
        setContactDetails({
          phone_number: data.phone_number || '',
          email: data.email || '',
          address: data.address || '',
          website: data.website || '',
          office_hours: data.office_hours || ''
        });
      }
    } catch (error) {
      console.error('Error fetching contact details:', error);
    }
  };

  const fetchFAQs = async () => {
    try {
      let query = supabase
        .from('school_faqs')
        .select('*')
        .eq('school_id', schoolData.id)
        .order('order_index', { ascending: true });

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      } else {
        query = query.is('branch_id', null);
      }

      const { data, error } = await query;
      if (error) {
        console.error('Error fetching FAQs:', error);
      } else {
        setFaqs(data || []);
      }
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    }
  };

  const saveContactDetails = async () => {
    try {
      const payload = {
        school_id: schoolData.id,
        branch_id: selectedBranchId || null,
        ...contactDetails,
        updated_at: new Date().toISOString()
      };

      // Check if record exists
      let query = supabase
        .from('school_contact_details')
        .select('id')
        .eq('school_id', schoolData.id);

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      } else {
        query = query.is('branch_id', null);
      }

      const { data: existing } = await query.maybeSingle();

      if (existing) {
        // Update existing
        const { error } = await supabase
          .from('school_contact_details')
          .update(payload)
          .eq('id', existing.id);

        if (error) throw error;
        alert('Contact details updated successfully!');
      } else {
        // Insert new
        payload.created_at = new Date().toISOString();
        const { error } = await supabase
          .from('school_contact_details')
          .insert(payload);

        if (error) throw error;
        alert('Contact details saved successfully!');
      }
    } catch (error) {
      console.error('Error saving contact details:', error);
      alert('Failed to save contact details: ' + error.message);
    }
  };

  const saveFAQ = async () => {
    try {
      if (!faqForm.question || !faqForm.answer) {
        alert('Please fill in both question and answer');
        return;
      }

      const payload = {
        school_id: schoolData.id,
        branch_id: selectedBranchId || null,
        question: faqForm.question.trim(),
        answer: faqForm.answer.trim(),
        order_index: editingFaq ? editingFaq.order_index : (faqs.length + 1),
        updated_at: new Date().toISOString()
      };

      if (editingFaq) {
        const { error } = await supabase
          .from('school_faqs')
          .update(payload)
          .eq('id', editingFaq.id);

        if (error) throw error;
        setSuccessMessage('FAQ updated successfully!');
        setShowSuccessPopup(true);
      } else {
        payload.created_at = new Date().toISOString();
        const { error } = await supabase
          .from('school_faqs')
          .insert(payload);

        if (error) throw error;
        setSuccessMessage('FAQ added successfully!');
        setShowSuccessPopup(true);
      }

      setShowFaqForm(false);
      setEditingFaq(null);
      setFaqForm({ question: '', answer: '' });
      fetchFAQs();

      // Auto-close success popup after 3 seconds
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);
    } catch (error) {
      console.error('Error saving FAQ:', error);
      alert('Failed to save FAQ: ' + error.message);
    }
  };

  const deleteFAQ = async (id) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;

    try {
      const { error } = await supabase
        .from('school_faqs')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchFAQs();
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      alert('Failed to delete FAQ: ' + error.message);
    }
  };

  const handleLogout = () => navigate('/schoollogin');

  if (loading) return <div className="loading-container"><div className="loading-spinner"></div><p>Loading...</p></div>;

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen}
      />
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />
      <div className="main-content" style={{ padding: '2rem', paddingLeft: '2.5rem', paddingRight: '2.5rem' }}>
        <div className="content-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', padding: '1.5rem 2rem', background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ flex: 1 }}>
            <h1 style={{ margin: 0, marginBottom: '0.5rem', fontSize: '1.75rem', color: '#2c3e50' }}>📞 Contact Us Management</h1>
            <p style={{ margin: 0, color: '#6b7280', fontSize: '0.95rem' }}>Manage contact details and FAQs for students and parents</p>
          </div>
        </div>

        {/* Contact Details Section */}
        <div className="classes-content compact full-width" style={{ marginBottom: '1.5rem', padding: '1.5rem', background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h2 style={{ marginTop: 0, marginBottom: '1.5rem', color: '#2c3e50' }}>Contact Details</h2>
          <div className="form-group">
            <label>Phone Number *</label>
            <input
              type="tel"
              value={contactDetails.phone_number}
              onChange={(e) => setContactDetails({...contactDetails, phone_number: e.target.value})}
              placeholder="+1234567890"
            />
          </div>
          <div className="form-group">
            <label>Email Address *</label>
            <input
              type="email"
              value={contactDetails.email}
              onChange={(e) => setContactDetails({...contactDetails, email: e.target.value})}
              placeholder="support@school.com"
            />
          </div>
          <div className="form-group">
            <label>Address</label>
            <textarea
              rows="3"
              value={contactDetails.address}
              onChange={(e) => setContactDetails({...contactDetails, address: e.target.value})}
              placeholder="School address"
            />
          </div>
          <div className="form-group">
            <label>Website</label>
            <input
              type="url"
              value={contactDetails.website}
              onChange={(e) => setContactDetails({...contactDetails, website: e.target.value})}
              placeholder="https://www.school.com"
            />
          </div>
          <div className="form-group">
            <label>Office Hours</label>
            <input
              type="text"
              value={contactDetails.office_hours}
              onChange={(e) => setContactDetails({...contactDetails, office_hours: e.target.value})}
              placeholder="Monday - Friday: 8:00 AM - 4:00 PM"
            />
          </div>
          <div className="form-actions">
            <button className="btn-primary" onClick={saveContactDetails}>Save Contact Details</button>
          </div>
        </div>

        {/* FAQs Section */}
        <div className="classes-content compact full-width" style={{ padding: '1.5rem', background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h2 style={{ margin: 0, color: '#2c3e50' }}>Frequently Asked Questions (FAQs)</h2>
            <button className="btn-primary" onClick={() => { setEditingFaq(null); setFaqForm({ question: '', answer: '' }); setShowFaqForm(true); }}>
              + Add FAQ
            </button>
          </div>

          {faqs.length === 0 ? (
            <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>No FAQs added yet. Click "+ Add FAQ" to create one.</p>
          ) : (
            <div className="faqs-list">
              {faqs.map((faq, index) => (
                <div key={faq.id} className="faq-item" style={{ marginBottom: '1rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ margin: '0 0 0.5rem 0', color: '#1f2937' }}>Q{index + 1}: {faq.question}</h4>
                      <p style={{ margin: 0, color: '#4b5563', lineHeight: '1.6' }}>{faq.answer}</p>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '1rem' }}>
                      <button className="icon-btn" onClick={() => { setEditingFaq(faq); setFaqForm({ question: faq.question, answer: faq.answer }); setShowFaqForm(true); }}>✏️</button>
                      <button className="icon-btn danger" onClick={() => deleteFAQ(faq.id)}>🗑️</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* FAQ Form Modal */}
        {showFaqForm && (
          <div className="modal-backdrop" onClick={() => { setShowFaqForm(false); setEditingFaq(null); setFaqForm({ question: '', answer: '' }); }}>
            <div className="modal-card" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{editingFaq ? 'Edit FAQ' : 'Add FAQ'}</h3>
                <button className="close-btn" onClick={() => { setShowFaqForm(false); setEditingFaq(null); setFaqForm({ question: '', answer: '' }); }}>×</button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Question *</label>
                  <input
                    type="text"
                    value={faqForm.question}
                    onChange={(e) => setFaqForm({...faqForm, question: e.target.value})}
                    placeholder="Enter question"
                  />
                </div>
                <div className="form-group">
                  <label>Answer *</label>
                  <textarea
                    rows="5"
                    value={faqForm.answer}
                    onChange={(e) => setFaqForm({...faqForm, answer: e.target.value})}
                    placeholder="Enter answer"
                  />
                </div>
                <div className="form-actions">
                  <button className="btn-primary" onClick={saveFAQ}>{editingFaq ? 'Update FAQ' : 'Add FAQ'}</button>
                  <button className="btn-secondary" onClick={() => { setShowFaqForm(false); setEditingFaq(null); setFaqForm({ question: '', answer: '' }); }}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Success Popup */}
        {showSuccessPopup && (
          <div 
            className="success-popup-overlay"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 10000,
              animation: 'fadeIn 0.3s ease-in'
            }}
            onClick={() => setShowSuccessPopup(false)}
          >
            <div 
              className="success-popup"
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '20px',
                padding: '2.5rem',
                maxWidth: '400px',
                width: '90%',
                textAlign: 'center',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                animation: 'slideUp 0.4s ease-out',
                position: 'relative',
                overflow: 'hidden'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Decorative circles */}
              <div style={{
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: '150px',
                height: '150px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.1)',
                zIndex: 0
              }}></div>
              <div style={{
                position: 'absolute',
                bottom: '-30px',
                left: '-30px',
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.1)',
                zIndex: 0
              }}></div>
              
              {/* Success Icon */}
              <div style={{
                position: 'relative',
                zIndex: 1,
                marginBottom: '1.5rem',
                animation: 'scaleIn 0.5s ease-out 0.2s both'
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  margin: '0 auto',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '3px solid white'
                }}>
                  <svg 
                    width="50" 
                    height="50" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="white" 
                    strokeWidth="3" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    style={{ animation: 'checkmark 0.6s ease-out 0.4s both' }}
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </div>
              </div>

              {/* Success Message */}
              <h3 style={{
                position: 'relative',
                zIndex: 1,
                color: 'white',
                fontSize: '1.5rem',
                fontWeight: '600',
                margin: '0 0 0.5rem 0',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
              }}>
                {successMessage}
              </h3>
              
              <p style={{
                position: 'relative',
                zIndex: 1,
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '0.95rem',
                margin: '0.5rem 0 0 0'
              }}>
                Your FAQ has been saved successfully!
              </p>

              {/* Close Button */}
              <button
                onClick={() => setShowSuccessPopup(false)}
                style={{
                  position: 'relative',
                  zIndex: 1,
                  marginTop: '1.5rem',
                  padding: '0.75rem 2rem',
                  background: 'white',
                  color: '#667eea',
                  border: 'none',
                  borderRadius: '25px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.05)';
                  e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.2)';
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add CSS animations */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            transform: translateY(30px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes scaleIn {
          from {
            transform: scale(0);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes checkmark {
          0% {
            stroke-dasharray: 0 50;
            stroke-dashoffset: 0;
          }
          100% {
            stroke-dasharray: 50 0;
            stroke-dashoffset: 0;
          }
        }
      `}</style>
    </div>
  );
}

export default ContactUs;
